import clamp from 'lodash/clamp';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import maxBy from 'lodash/maxBy';
import omit from 'lodash/omit';
import range from 'lodash/range';
import sortBy from 'lodash/sortBy';
import sumBy from 'lodash/sumBy';
import uniqWith from 'lodash/uniqWith';
import {
  bottom,
  cloneLayout,
  type Compactor,
  type ConstraintContext,
  getFirstCollision,
  gridBounds,
  type Layout,
  type LayoutConstraint,
  type LayoutItem,
  minMaxSize,
  type ResizeHandleAxis,
  sortLayoutItemsByRowCol,
  verticalCompactor,
} from 'react-grid-layout/core';

export const GRID_COLUMNS = 12;

export const GRID_ROW_HEIGHT_IN_PIXELS = 30;

export const GRID_GAP_IN_PIXELS = 20;

export const MIN_WIDGET_WIDTH = 3;

export const MIN_WIDGET_HEIGHT = 5;

export const MAX_WIDGET_HEIGHT = 16;

export const DROPPING_ITEM_ID = '__dropping-elem__';

const DROP_SNAP_RADIUS = 2;

export const ALL_RESIZE_HANDLES: ResizeHandleAxis[] = ['s', 'w', 'e', 'n', 'sw', 'nw', 'se', 'ne'];

const WEST_HANDLES: ResizeHandleAxis[] = ['w', 'nw', 'sw'];

const NORTH_HANDLES: ResizeHandleAxis[] = ['n', 'nw', 'ne'];

const WIDTH_PRESET_LABELS: Partial<Record<number, string>> = { 3: '¼', 4: '⅓', 6: '½', 8: '⅔', 9: '¾' };

const HEIGHT_PRESET_LABELS: Partial<Record<number, string>> = { 6: 'S', 8: 'M', 10: 'L', 16: 'XL' };

export const WIDTH_PRESETS: number[] = [3, 4, 6, 8, 9, GRID_COLUMNS];

export const HEIGHT_PRESETS: number[] = [6, 8, 10, 16];

export type WidgetNudgeDirection = 'up' | 'down' | 'left' | 'right';

export interface WidgetFootprint {
  w: number;
  h: number;
}

export interface WidgetFootprintLimits {
  minW?: number;
  minH?: number;
}

export interface GridRect extends WidgetFootprint {
  x: number;
  y: number;
}

export type GridHole = GridRect;

export interface WidgetGridItem<Id extends string = string> extends GridRect {
  id: Id;
  pinned?: boolean;
}

export type WidgetFootprintLimitsOf<Id extends string> = (id: Id) => WidgetFootprintLimits;

export type GridCell = Pick<GridRect, 'x' | 'y'>;

export interface GridPointer {
  left: number;
  top: number;
}

interface DropCandidate {
  cell: GridCell;
  moved: number;
  distance: number;
}

export interface WidgetGridInteraction {
  id: string;
  vacated: GridRect | null;
  start: Map<string, GridCell>;
}

interface SwappedLayout {
  layout: LayoutItem[];
  swappedIds: string[];
}

const NO_LIMITS = (): WidgetFootprintLimits => ({});

export const heightInPixels = (h: number): number => h * GRID_ROW_HEIGHT_IN_PIXELS + (h - 1) * GRID_GAP_IN_PIXELS;

export const gridHeightInPixels = (rows: number): number => (rows > 0 ? heightInPixels(rows) : 0);

export const columnWidthOf = (gridWidth: number): number =>
  (gridWidth - GRID_GAP_IN_PIXELS * (GRID_COLUMNS - 1)) / GRID_COLUMNS;

export const widthInPixels = (w: number, columnWidth: number): number => w * columnWidth + (w - 1) * GRID_GAP_IN_PIXELS;

export const isWidthPreset = (w: number): boolean => WIDTH_PRESETS.includes(w);

export const isHeightPreset = (h: number): boolean => HEIGHT_PRESETS.includes(h);

export const isPresetFootprint = ({ w, h }: WidgetFootprint): boolean => isWidthPreset(w) && isHeightPreset(h);

export const formatWidthLabel = (w: number, fullWidthLabel: string): string =>
  w === GRID_COLUMNS ? fullWidthLabel : (WIDTH_PRESET_LABELS[w] ?? `${w}/${GRID_COLUMNS}`);

export const formatHeightLabel = (h: number): string => HEIGHT_PRESET_LABELS[h] ?? `${heightInPixels(h)} px`;

const clampBetween = (value: number, lower: number, upper: number): number => clamp(Math.round(value), lower, upper);

export const minFootprintOf = (limits: WidgetFootprintLimits = {}): WidgetFootprint => ({
  w: Math.max(MIN_WIDGET_WIDTH, limits.minW ?? MIN_WIDGET_WIDTH),
  h: Math.max(MIN_WIDGET_HEIGHT, limits.minH ?? MIN_WIDGET_HEIGHT),
});

export const clampFootprint = ({ w, h }: WidgetFootprint, limits: WidgetFootprintLimits = {}): WidgetFootprint => {
  const minimum = minFootprintOf(limits);
  return { w: clampBetween(w, minimum.w, GRID_COLUMNS), h: clampBetween(h, minimum.h, MAX_WIDGET_HEIGHT) };
};

export const bottomOf = (items: GridRect[]): number => Math.max(0, ...items.map((item) => item.y + item.h));

const lowestRowOf = (items: readonly GridRect[]): number =>
  sumBy(items, (item) => item.h) + MAX_WIDGET_HEIGHT * items.length;

export const stackedItems = <Id extends string>(items: WidgetGridItem<Id>[]): WidgetGridItem<Id>[] =>
  sortBy(items, [(item) => item.y, (item) => item.x]);

const isPinned = (item: WidgetGridItem): boolean => item.pinned ?? false;

const withPinned = <Id extends string>(item: WidgetGridItem<Id>, pinned: boolean): WidgetGridItem<Id> =>
  pinned ? { ...item, pinned: true } : omit(item, 'pinned');

const overlapsRect = (first: GridRect, second: GridRect): boolean =>
  first.x < second.x + second.w &&
  second.x < first.x + first.w &&
  first.y < second.y + second.h &&
  second.y < first.y + first.h;

const overlapsColumns = (first: GridRect, second: GridRect): boolean =>
  first.x < second.x + second.w && second.x < first.x + first.w;

const overlapsRows = (first: GridRect, second: GridRect): boolean =>
  first.y < second.y + second.h && second.y < first.y + first.h;

const hitsPinned = <Id extends string>(items: WidgetGridItem<Id>[], candidate: WidgetGridItem<Id>): boolean =>
  items.some((item) => isPinned(item) && item.id !== candidate.id && overlapsRect(item, candidate));

const sameOrNext = <Id extends string>(
  items: WidgetGridItem<Id>[],
  next: WidgetGridItem<Id>[]
): WidgetGridItem<Id>[] => (isEqual(next, stackedItems(items)) ? items : next);

const settledRow = <Rect extends GridRect>(placed: Rect[], item: Rect): number => {
  const blocker = maxBy(
    placed.filter((other) => overlapsRect(other, item)),
    (other) => other.y + other.h
  );
  return isNil(blocker) ? item.y : settledRow(placed, { ...item, y: blocker.y + blocker.h });
};

const settleRects = <Rect extends GridRect>(
  rects: Rect[],
  isAnchored: (rect: Rect) => boolean,
  ordered: (rects: Rect[]) => Rect[]
): Rect[] =>
  ordered(rects.filter((rect) => !isAnchored(rect))).reduce(
    (placed: Rect[], rect) => [...placed, { ...rect, y: settledRow(placed, rect) }],
    rects.filter(isAnchored)
  );

const settleItems = <Id extends string>(items: WidgetGridItem<Id>[], anchoredIds: Id[]): WidgetGridItem<Id>[] =>
  stackedItems(settleRects(items, (item) => isPinned(item) || anchoredIds.includes(item.id), stackedItems));

export const resolveItems = <Id extends string>(items: WidgetGridItem<Id>[]): WidgetGridItem<Id>[] =>
  settleItems(items, []);

export const compactItems = <Id extends string>(items: WidgetGridItem<Id>[]): WidgetGridItem<Id>[] => {
  const layout = cloneLayout(
    items.map((item) => ({ i: item.id, x: item.x, y: item.y, w: item.w, h: item.h, static: isPinned(item) }))
  );
  const compacted = new Map(verticalCompactor.compact(layout, GRID_COLUMNS).map((entry) => [entry.i, entry]));
  return stackedItems(
    items.map((item) => {
      const entry = compacted.get(item.id);
      return isNil(entry) ? item : { ...item, x: entry.x, y: entry.y };
    })
  );
};

export const canCompactItems = <Id extends string>(items: WidgetGridItem<Id>[]): boolean =>
  !isEqual(compactItems(items), stackedItems(items));

export const normaliseItems = <Id extends string>(
  items: WidgetGridItem<Id>[],
  limitsOf: WidgetFootprintLimitsOf<Id> = NO_LIMITS
): WidgetGridItem<Id>[] => {
  const sized = items.map((item) => ({ ...item, ...clampFootprint(item, limitsOf(item.id)) }));
  const lowestRow = lowestRowOf(sized);
  const clamped = sized.map((item) =>
    withPinned(
      {
        id: item.id,
        x: clampBetween(item.x, 0, GRID_COLUMNS - item.w),
        y: clampBetween(item.y, 0, lowestRow),
        w: item.w,
        h: item.h,
      },
      isPinned(item)
    )
  );
  const withoutPinnedOverlaps = stackedItems(clamped).reduce((acc: WidgetGridItem<Id>[], item) => {
    const overlapsAnotherPin = isPinned(item) && hitsPinned(acc, item);
    return [...acc, overlapsAnotherPin ? withPinned(item, false) : item];
  }, []);
  return resolveItems(withoutPinnedOverlaps);
};

export const findFreeSlot = (items: GridRect[], footprint: WidgetFootprint): Pick<GridRect, 'x' | 'y'> => {
  const lastRow = bottomOf(items);
  const columns = range(0, GRID_COLUMNS - footprint.w + 1);
  const slot = range(0, lastRow + 1).reduce((found: Pick<GridRect, 'x' | 'y'> | null, y) => {
    if (!isNil(found)) {
      return found;
    }
    const x = columns.find((column) =>
      items.every((item) => !overlapsRect(item, { x: column, y, w: footprint.w, h: footprint.h }))
    );
    return isNil(x) ? null : { x, y };
  }, null);
  return slot ?? { x: 0, y: lastRow };
};

export const placeItem = <Id extends string>(
  items: WidgetGridItem<Id>[],
  item: WidgetGridItem<Id>
): WidgetGridItem<Id>[] => {
  const others = items.filter((other) => other.id !== item.id);
  return stackedItems([...others, { ...item, ...findFreeSlot(others, item) }]);
};

const anchorItems = <Id extends string>(
  items: WidgetGridItem<Id>[],
  placed: WidgetGridItem<Id>[]
): WidgetGridItem<Id>[] => {
  const placedIds = placed.map((item) => item.id);
  const others = items.filter((item) => !placedIds.includes(item.id));
  return settleItems([...others, ...placed], placedIds);
};

export const insertItemAt = <Id extends string>(
  items: WidgetGridItem<Id>[],
  item: WidgetGridItem<Id>
): WidgetGridItem<Id>[] => {
  const others = items.filter((other) => other.id !== item.id);
  return hitsPinned(others, item) ? placeItem(others, item) : anchorItems(others, [item]);
};

export const dropCellAt = (pointer: GridPointer, footprint: WidgetFootprint, gridWidth: number): GridCell => {
  const columnWidth = columnWidthOf(gridWidth);
  const column = (pointer.left - widthInPixels(footprint.w, columnWidth) / 2) / (columnWidth + GRID_GAP_IN_PIXELS);
  const row = (pointer.top - heightInPixels(footprint.h) / 2) / (GRID_ROW_HEIGHT_IN_PIXELS + GRID_GAP_IN_PIXELS);
  return { x: clampBetween(column, 0, GRID_COLUMNS - footprint.w), y: Math.max(0, Math.round(row)) };
};

export const previewDrop = <Id extends string>(
  items: WidgetGridItem<Id>[],
  footprint: WidgetFootprint,
  cell: GridCell
): WidgetGridItem<string>[] => insertItemAt<string>(items, { id: DROPPING_ITEM_ID, ...cell, ...footprint });

const distanceBetween = (first: GridCell, second: GridCell): number =>
  Math.abs(first.x - second.x) + Math.abs(first.y - second.y);

const dropCandidate = <Id extends string>(
  items: WidgetGridItem<Id>[],
  footprint: WidgetFootprint,
  pointer: GridCell,
  cell: GridCell
): DropCandidate | null => {
  const preview = previewDrop(items, footprint, cell);
  const incoming = preview.find((item) => item.id === DROPPING_ITEM_ID);
  if (isNil(incoming) || distanceBetween(incoming, cell) > 0) {
    return null;
  }
  const before = new Map<string, WidgetGridItem<Id>>(items.map((item) => [item.id, item]));
  const moved = preview.filter((item) => {
    const previous = before.get(item.id);
    return !isNil(previous) && distanceBetween(item, previous) > 0;
  }).length;
  return { cell, moved, distance: distanceBetween(cell, pointer) };
};

export const snapDropCell = <Id extends string>(
  items: WidgetGridItem<Id>[],
  footprint: WidgetFootprint,
  pointer: GridCell
): GridCell => {
  const offsets = range(-DROP_SNAP_RADIUS, DROP_SNAP_RADIUS + 1);
  const cells = uniqWith(
    offsets.flatMap((dy) =>
      offsets.map((dx) => ({
        x: clampBetween(pointer.x + dx, 0, GRID_COLUMNS - footprint.w),
        y: Math.max(0, pointer.y + dy),
      }))
    ),
    isEqual
  );
  const candidates = compact(cells.map((cell) => dropCandidate(items, footprint, pointer, cell)));
  const [best] = sortBy(candidates, [(candidate) => candidate.moved, (candidate) => candidate.distance]);
  return best?.cell ?? pointer;
};

export const removeItem = <Id extends string>(items: WidgetGridItem<Id>[], id: Id): WidgetGridItem<Id>[] =>
  stackedItems(items.filter((item) => item.id !== id));

export const setItemPinned = <Id extends string>(
  items: WidgetGridItem<Id>[],
  id: Id,
  pinned: boolean
): WidgetGridItem<Id>[] =>
  sameOrNext(items, stackedItems(items.map((item) => (item.id === id ? withPinned(item, pinned) : item))));

export const resizeItem = <Id extends string>(
  items: WidgetGridItem<Id>[],
  id: Id,
  footprint: WidgetFootprint,
  limits: WidgetFootprintLimits = {}
): WidgetGridItem<Id>[] => {
  const item = items.find((candidate) => candidate.id === id);
  if (isNil(item) || isPinned(item)) {
    return items;
  }
  const clamped = clampFootprint(footprint, limits);
  const resized = { ...item, ...clamped, x: Math.min(item.x, GRID_COLUMNS - clamped.w) };
  const others = items.filter((other) => other.id !== id);
  if (hitsPinned(others, resized)) {
    return items;
  }
  return sameOrNext(items, anchorItems(others, [resized]));
};

export const KEYBOARD_RESIZE_STEPS: Partial<Record<string, WidgetFootprint>> = {
  ArrowLeft: { w: -1, h: 0 },
  ArrowRight: { w: 1, h: 0 },
  ArrowUp: { w: 0, h: -1 },
  ArrowDown: { w: 0, h: 1 },
};

export const KEYBOARD_RESIZE_SHORTCUTS = Object.keys(KEYBOARD_RESIZE_STEPS)
  .map((key) => `Shift+${key}`)
  .join(' ');

export const resizeItemBy = <Id extends string>(
  items: WidgetGridItem<Id>[],
  id: Id,
  step: WidgetFootprint,
  limits: WidgetFootprintLimits = {}
): WidgetGridItem<Id>[] => {
  const item = items.find((candidate) => candidate.id === id);
  return isNil(item) ? items : resizeItem(items, id, { w: item.w + step.w, h: item.h + step.h }, limits);
};

export const canResizeItem = <Id extends string>(
  items: WidgetGridItem<Id>[],
  id: Id,
  footprint: WidgetFootprint,
  limits: WidgetFootprintLimits = {}
): boolean => resizeItem(items, id, footprint, limits) !== items;

const moveTogether = <Id extends string>(
  items: WidgetGridItem<Id>[],
  placed: WidgetGridItem<Id>[]
): WidgetGridItem<Id>[] => {
  const placedIds = placed.map((item) => item.id);
  const others = items.filter((item) => !placedIds.includes(item.id));
  if (placed.some((item) => hitsPinned(others, item))) {
    return items;
  }
  return sameOrNext(items, anchorItems(others, placed));
};

const sharedLength = (start: number, length: number, otherStart: number, otherLength: number): number =>
  Math.min(start + length, otherStart + otherLength) - Math.max(start, otherStart);

const touchingNeighbour = <Id extends string>(
  item: WidgetGridItem<Id>,
  others: WidgetGridItem<Id>[],
  direction: WidgetNudgeDirection
): WidgetGridItem<Id> | undefined => {
  switch (direction) {
    case 'up':
    case 'down': {
      const edge = direction === 'up' ? item.y : item.y + item.h;
      const touching = others.filter(
        (other) => overlapsColumns(other, item) && (direction === 'up' ? other.y + other.h : other.y) === edge
      );
      return maxBy(touching, (other) => sharedLength(item.x, item.w, other.x, other.w));
    }
    case 'left':
    case 'right': {
      const edge = direction === 'left' ? item.x : item.x + item.w;
      const touching = others.filter(
        (other) => overlapsRows(other, item) && (direction === 'left' ? other.x + other.w : other.x) === edge
      );
      return maxBy(touching, (other) => sharedLength(item.y, item.h, other.y, other.h));
    }
    default:
      return undefined;
  }
};

const swappedWith = <Id extends string>(
  item: WidgetGridItem<Id>,
  neighbour: WidgetGridItem<Id>,
  direction: WidgetNudgeDirection
): WidgetGridItem<Id>[] => {
  switch (direction) {
    case 'up':
      return [
        { ...item, y: neighbour.y },
        { ...neighbour, y: neighbour.y + item.h },
      ];
    case 'down':
      return [
        { ...item, y: item.y + neighbour.h },
        { ...neighbour, y: item.y },
      ];
    case 'left':
      return [
        { ...item, x: neighbour.x },
        { ...neighbour, x: item.x + item.w - neighbour.w },
      ];
    case 'right':
      return [
        { ...item, x: item.x + neighbour.w },
        { ...neighbour, x: item.x },
      ];
    default:
      return [item, neighbour];
  }
};

const steppedBy = <Id extends string>(
  item: WidgetGridItem<Id>,
  direction: WidgetNudgeDirection
): WidgetGridItem<Id> | null => {
  switch (direction) {
    case 'up':
      return item.y > 0 ? { ...item, y: item.y - 1 } : null;
    case 'down':
      return { ...item, y: item.y + 1 };
    case 'left':
      return item.x > 0 ? { ...item, x: item.x - 1 } : null;
    case 'right':
      return item.x + item.w < GRID_COLUMNS ? { ...item, x: item.x + 1 } : null;
    default:
      return null;
  }
};

export const nudgeItem = <Id extends string>(
  items: WidgetGridItem<Id>[],
  id: Id,
  direction: WidgetNudgeDirection
): WidgetGridItem<Id>[] => {
  const item = items.find((candidate) => candidate.id === id);
  if (isNil(item) || isPinned(item)) {
    return items;
  }
  const others = items.filter((other) => other.id !== id);
  const neighbour = touchingNeighbour(item, others, direction);
  if (!isNil(neighbour)) {
    return isPinned(neighbour) ? items : moveTogether(items, swappedWith(item, neighbour, direction));
  }
  const stepped = steppedBy(item, direction);
  return isNil(stepped) ? items : moveTogether(items, [stepped]);
};

export const findGridHoles = (items: GridRect[], minimum: WidgetFootprint): GridHole[] => {
  const lastRow = bottomOf(items);
  const isFree = (x: number, y: number) => !items.some((item) => overlapsRect(item, { x, y, w: 1, h: 1 }));
  const cells = range(0, lastRow).flatMap((y) => range(0, GRID_COLUMNS).map((x) => ({ x, y })));
  const holes = cells.reduce((acc: GridHole[], { x, y }) => {
    const inHole = (column: number) => acc.some((hole) => overlapsRect(hole, { x: column, y, w: 1, h: 1 }));
    if (!isFree(x, y) || inHole(x)) {
      return acc;
    }
    const blockedColumn = range(x, GRID_COLUMNS).find((column) => !isFree(column, y) || inHole(column));
    const w = (blockedColumn ?? GRID_COLUMNS) - x;
    const blockedRow = range(y, lastRow).find((row) => range(x, x + w).some((column) => !isFree(column, row)));
    const h = (blockedRow ?? lastRow) - y;
    return [...acc, { x, y, w, h }];
  }, []);
  return holes.filter((hole) => hole.w >= minimum.w && hole.h >= minimum.h);
};

const proposedRect = (item: LayoutItem, w: number, h: number, handle: ResizeHandleAxis): LayoutItem => ({
  i: item.i,
  x: WEST_HANDLES.includes(handle) ? item.x + item.w - w : item.x,
  y: NORTH_HANDLES.includes(handle) ? item.y + item.h - h : item.y,
  w,
  h,
});

const pinnedOthers = (item: LayoutItem, context: ConstraintContext): LayoutItem[] =>
  context.layout.filter((other) => (other.static ?? false) && other.i !== item.i);

const nearestFreeCell = (item: LayoutItem, cell: GridCell, pinned: LayoutItem[], columns: number): GridCell => {
  const cells = range(0, bottom(pinned) + 1).flatMap((y) => range(0, columns - item.w + 1).map((x) => ({ x, y })));
  const free = cells.filter((candidate) => isNil(getFirstCollision(pinned, { ...item, ...candidate })));
  const [nearest] = sortBy(free, [
    (candidate) => distanceBetween(candidate, cell),
    (candidate) => Math.abs(candidate.y - cell.y),
  ]);
  return nearest ?? cell;
};

export const pinnedGuardConstraint: LayoutConstraint = {
  name: 'pinnedGuard',
  constrainPosition: (item: LayoutItem, x: number, y: number, context: ConstraintContext) => {
    const cell = { x, y: Math.min(y, lowestRowOf(context.layout)) };
    const pinned = pinnedOthers(item, context);
    return isNil(getFirstCollision(pinned, { ...item, ...cell }))
      ? cell
      : nearestFreeCell(item, cell, pinned, context.cols);
  },
  constrainSize: (item: LayoutItem, w: number, h: number, handle: ResizeHandleAxis, context: ConstraintContext) => {
    const pinned = pinnedOthers(item, context);
    const isFree = (width: number, height: number) =>
      isNil(getFirstCollision(pinned, proposedRect(item, width, height, handle)));
    if (isEmpty(pinned) || isFree(w, h)) {
      return { w, h };
    }
    const candidates = range(w, Math.min(w, item.w) - 1, -1).flatMap((width) =>
      range(h, Math.min(h, item.h) - 1, -1).map((height) => ({ w: width, h: height }))
    );
    const free = candidates.filter((candidate) => isFree(candidate.w, candidate.h));
    return maxBy(free, (candidate) => candidate.w * candidate.h) ?? { w: Math.min(w, item.w), h: Math.min(h, item.h) };
  },
};

export const GRID_CONSTRAINTS: LayoutConstraint[] = [gridBounds, minMaxSize, pinnedGuardConstraint];

export const toGridLayout = <Id extends string>(
  items: WidgetGridItem<Id>[],
  limitsOf: WidgetFootprintLimitsOf<Id>,
  editable: boolean
): LayoutItem[] =>
  items.map((item) => {
    const minimum = minFootprintOf(limitsOf(item.id));
    const pinned = isPinned(item);
    return {
      i: item.id,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
      minW: minimum.w,
      minH: minimum.h,
      maxW: GRID_COLUMNS,
      maxH: MAX_WIDGET_HEIGHT,
      static: pinned,
      resizeHandles: pinned || !editable ? [] : ALL_RESIZE_HANDLES,
    };
  });

export const withDropPreview = (layout: LayoutItem[], preview: WidgetGridItem<string>[]): LayoutItem[] => {
  const entries = new Map(layout.map((entry) => [entry.i, entry]));
  return preview.map((item) => {
    const entry = entries.get(item.id);
    if (isNil(entry)) {
      return { i: item.id, x: item.x, y: item.y, w: item.w, h: item.h, static: true, resizeHandles: [] };
    }
    return { ...entry, x: item.x, y: item.y };
  });
};

export const fromGridLayout = <Id extends string>(
  layout: Layout,
  previous: WidgetGridItem<Id>[]
): WidgetGridItem<Id>[] => {
  const previousById = new Map<string, WidgetGridItem<Id>>(previous.map((item) => [item.id, item]));
  const items = layout.reduce((acc: WidgetGridItem<Id>[], entry) => {
    const known = previousById.get(entry.i);
    if (entry.i === DROPPING_ITEM_ID || isNil(known)) {
      return acc;
    }
    return [...acc, withPinned({ id: known.id, x: entry.x, y: entry.y, w: entry.w, h: entry.h }, isPinned(known))];
  }, []);
  const next = resolveItems(items);
  return isEqual(next, stackedItems(previous)) ? previous : next;
};

export const startInteraction = (layout: Layout, active: LayoutItem, swappable: boolean): WidgetGridInteraction => ({
  id: active.i,
  vacated: swappable ? { x: active.x, y: active.y, w: active.w, h: active.h } : null,
  start: new Map(layout.map((entry) => [entry.i, { x: entry.x, y: entry.y }])),
});

const startRowOf = (entry: LayoutItem, interaction: WidgetGridInteraction): number =>
  interaction.start.get(entry.i)?.y ?? entry.y;

const settleLayout = (layout: Layout, anchoredId: string | null): LayoutItem[] => {
  const settled = new Map(
    settleRects([...layout], (entry) => (entry.static ?? false) || entry.i === anchoredId, sortLayoutItemsByRowCol).map(
      (entry) => [entry.i, entry]
    )
  );
  return layout.map((entry) => settled.get(entry.i) ?? entry);
};

const slotWithin = (layout: LayoutItem[], item: LayoutItem, area: GridRect): GridCell | null => {
  if (item.w > area.w || item.h > area.h) {
    return null;
  }
  const columns = sortBy(range(area.x, area.x + area.w - item.w + 1), (x) => Math.abs(x - item.x));
  const rows = range(area.y, area.y + area.h - item.h + 1);
  return rows.reduce((found: GridCell | null, y) => {
    if (!isNil(found)) {
      return found;
    }
    const x = columns.find((column) => isNil(getFirstCollision(layout, { ...item, x: column, y })));
    return isNil(x) ? null : { x, y };
  }, null);
};

const isDisplaced = (entry: LayoutItem, interaction: WidgetGridInteraction): boolean =>
  entry.i !== interaction.id && !(entry.static ?? false) && entry.y > startRowOf(entry, interaction);

const moveDisplacedIntoVacated = (
  layout: LayoutItem[],
  interaction: WidgetGridInteraction,
  vacated: GridRect
): SwappedLayout =>
  sortLayoutItemsByRowCol(layout)
    .filter((item) => isDisplaced(item, interaction))
    .reduce(
      (swapped: SwappedLayout, displaced) => {
        const slot = slotWithin(swapped.layout, displaced, vacated);
        if (isNil(slot)) {
          return swapped;
        }
        return {
          layout: swapped.layout.map((entry) => (entry.i === displaced.i ? { ...entry, ...slot } : entry)),
          swappedIds: [...swapped.swappedIds, displaced.i],
        };
      },
      { layout, swappedIds: [] }
    );

const restoreDisplaced = (
  layout: LayoutItem[],
  interaction: WidgetGridInteraction,
  swappedIds: string[]
): LayoutItem[] =>
  sortBy(
    layout.filter((entry) => isDisplaced(entry, interaction) && !swappedIds.includes(entry.i)),
    [(entry) => startRowOf(entry, interaction), (entry) => entry.x]
  ).reduce((current: LayoutItem[], displaced) => {
    const row = range(startRowOf(displaced, interaction), displaced.y).find((y) =>
      isNil(getFirstCollision(current, { ...displaced, y }))
    );
    return isNil(row) ? current : current.map((entry) => (entry.i === displaced.i ? { ...entry, y: row } : entry));
  }, layout);

const holdLayout = (layout: Layout, interaction: WidgetGridInteraction): LayoutItem[] =>
  layout.map((entry) => {
    const start = interaction.start.get(entry.i);
    return entry.i === interaction.id || isNil(start) ? { ...entry } : { ...entry, ...start };
  });

export const resolveInteraction = (layout: Layout, interaction: WidgetGridInteraction): LayoutItem[] => {
  const settled = settleLayout(holdLayout(layout, interaction), interaction.id);
  const { layout: swapped, swappedIds } = isNil(interaction.vacated)
    ? { layout: settled, swappedIds: [] }
    : moveDisplacedIntoVacated(settled, interaction, interaction.vacated);
  return settleLayout(restoreDisplaced(swapped, interaction, swappedIds), interaction.id);
};

export const createFreeCompactor = (getInteraction: () => WidgetGridInteraction | null): Compactor => ({
  type: 'vertical',
  allowOverlap: false,
  compact: (layout: Layout): Layout => {
    const interaction = getInteraction();
    return isNil(interaction) ? settleLayout(cloneLayout(layout), null) : resolveInteraction(layout, interaction);
  },
});
